import React, { useState, useEffect } from "react";
import { FaRoute } from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";
import { MdOutlineManageSearch } from "react-icons/md";
import { RiMenuFold2Fill, RiMenuUnfold2Fill, RiStockFill } from "react-icons/ri";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TbMapPinStar } from "react-icons/tb";

import {
  Boxes,
  LayoutDashboard,
  ClipboardList,
  FileBarChart,
  Warehouse,
  Calendar1,
  Box,
  Factory,
  Store,
  PackageSearch,
  Package,
  Activity,
  FileText,
  BarChart2,
  GalleryHorizontal,
  FileCog,
  Truck,Touchpad ,
  Columns3Cog,
  Layers
} from "lucide-react";

import "./Sidebar.css";

const menus = [
  {
    title: "Masters",
    icon: LayoutDashboard,
    isLink: false,
    items: [  
      { 
        title: "FG Master", 
        isLink: false,
        items: [
          { title: "SKU Master", url: "/sku-master", icon: Box },
          { title: "Source Master", url: "/source-master", icon: Factory },
          { title: "Destination Store Master", url: "/destination-master", icon: Store },
          { title: "Source Destination Mapping", url: "/source-destination-mapping", icon: FaRoute },
          { title: "WH SKU Category Norms", url: "/wh_sku_category_norms", icon: ClipboardList },
          { title: "Masters & Demand Plan", url: "/dp-masters", icon: FileText },
          { title: "Calendar Master", url: "/calender-masters", icon: Calendar1 },
          { title: "Batch Master", url: "/batch-master", icon: Columns3Cog },
          { title: "Pincode Mapping", url: "/pin-mapping", icon: TbMapPinStar },
        ]
      },
      { 
        title: "PM Master", 
        isLink: false,
        items: [
          { title: "PM Sku Master", url: "/pm-sku-master", icon: Box },
          { title: "Conversion Metrics", url: "/conversion-metric", icon: Factory },
          { title: "PM FG Mapping", url: "/pm-mapping", icon: FaRoute },
          { title: "Vendor Master", url: "/pm-vendor", icon: FileText },
          { title: "WH-Vendor Mapping", url: "/warehouse-vendor", icon: Columns3Cog },
          { title: "Warehouse Master", url: "/pm-warehouse", icon: Columns3Cog },
        ]
      }
    ],
  },
   {
    title: "Inventory Management",
    icon: PackageSearch,
    isLink: false,
    items: [
      { 
        title: "FG Inventory", 
        isLink: false,
        items: [
          { title: "FG Stock Status", url: "/fg-stock-status", icon: Boxes },
          { title: "Finished Goods Inventory", url: "/finished-goods-inventory", icon: Truck },
          { title: "Inventory Dashboards", url: "/inventory-dashboards", icon: Package },
          { title: "Historic Data", url: "/historic-data", icon: BarChart2 },
          { title: "PP Tracking Dashboard", url: "/pp-tracking-dashboard", icon: Touchpad},
          { title: "FG Analytics Dashboard", url: "/dashboards", icon: FileText},


        ]
      },
      { 
        title: "PM Inventory", 
        isLink: false,
        items: [
          { title: "PO Data", url: "/pm-po-data", icon: Layers },
          { title: "PM Stock Status", url: "/pm-stock-status", icon: Boxes },
          { title: "Packing Material Inventory", url: "/pm-inventory", icon: Truck },
        ]
      }
    ],
  },
  { 
    title: "Indentation Management",
    icon: MdOutlineManageSearch,
    isLink: false,
    items: [
      { title: "Cycle Dates", url: "/cycle-dates", icon: FiCalendar },
      { title: "Darkstore Indentation", url: "/darkstore-indentation", icon: Layers },
      { title: "Warehouse Indentation", url: "/warehouse-indentation", icon: Warehouse },

    ],
  },
  {
    title: " System Configurations",
    icon: FileCog,
    url: "/inventory-configurations",
    isLink: true,
    items: [],
  },
];

const Sidebar = ({ mainBgColor }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openParents, setOpenParents] = useState([]);
  const [openSubParents, setOpenSubParents] = useState([]);
  const location = useLocation();

  const handleMenuClick = () => {
    setShowTooltip(false);
    sessionStorage.setItem("isSidebarOpen", JSON.stringify(!isSidebarOpen));
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const isOpen = JSON.parse(sessionStorage.getItem("isSidebarOpen")) || false;
    const handleResize = () => {
      if (window.innerWidth < 768) setIsSidebarOpen(isOpen);
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    // Set active parent menus based on current path
    menus.forEach((item, index) => {
      if (item.items.some((sub) => 
        (sub.url === location.pathname) || 
        (sub.items && sub.items.some(subSub => subSub.url === location.pathname))
      ) || item.url === location.pathname) {
        setOpenParents((prev) => [...new Set([...prev, index])]);
      }
    });

    // Set active sub-parent menus for Masters
    menus.forEach((parent, parentIndex) => {
      parent.items.forEach((subItem, subIndex) => {
        if (subItem.items && subItem.items.some(subSub => subSub.url === location.pathname)) {
          setOpenSubParents((prev) => [...new Set([...prev, `${parentIndex}-${subIndex}`])]);
        }
      });
    });

    return () => window.removeEventListener("resize", handleResize);
  }, [location.pathname]);

  const isActive = (url) => location.pathname === url;
  
  const isParentActive = (item) => 
    isActive(item.url) || 
    item.items.some((sub) => 
      isActive(sub.url) || 
      (sub.items && sub.items.some(subSub => isActive(subSub.url)))
    );

  const isSubParentActive = (subItem) =>
    isActive(subItem.url) ||
    (subItem.items && subItem.items.some(subSub => isActive(subSub.url)));

  const toggleParent = (index) => {
    setOpenParents((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const toggleSubParent = (parentIndex, subIndex) => {
    const key = `${parentIndex}-${subIndex}`;
    setOpenSubParents((prev) => (prev.includes(key) ? prev.filter((i) => i !== key) : [...prev, key]));
  };

  const handleMouseLeave = () => {
    sessionStorage.setItem("isSidebarOpen", JSON.stringify(false));
    setIsSidebarOpen(false);
  };

  return isSidebarVisible ? (
    <div
      className={`sidebar ${isSidebarOpen ? "open" : "closed"}`}
      style={!isSidebarOpen ? { backgroundColor: mainBgColor } : {}}
      onMouseLeave={handleMouseLeave}
    >
      <div className="sidebar-header">
        {isSidebarOpen && (
          <div className="sidebar-logo">
            The <span>Whole</span> Truth
          </div>
        )}
        <div className="menu-toggle-wrapper" onClick={handleMenuClick}>
          {showTooltip && (
            <div
              className="menu-tooltip"
              style={{ top: isSidebarOpen ? "100px" : "90px", left: isSidebarOpen ? "140px" : "17px" }}
            >
              {!isSidebarOpen ? "Open Menu" : "Close Menu"}
            </div>
          )}
          <div className="menu-toggle">
              <div className={`menu-toggle-diamond-wrapper ${!isSidebarOpen ? "closed" : ""}`}></div>
                <div className={`menu-icon  ${!isSidebarOpen ? "closed" : ""}`}>
                    {!isSidebarOpen ? (
                      <RiMenuFold2Fill
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        size={18}
                        style={{ color: "#991548", zIndex: 1 }}
                      />
                    ) : (
                      <RiMenuUnfold2Fill
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        size={17}
                        style={{ color: "#991548", zIndex: 1 }}
                      />
                    )}
                </div>
          </div>
        </div>
      </div>

      {isSidebarOpen && (
        <nav className="sidebar-nav">
          {menus.map((item, index) => (
            <div key={item.title}>
              <div
                className={`sidebar-item ${isParentActive(item) ? "active" : "inactive"}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (item.items && !item.isLink) toggleParent(index);
                }}
              >
                <Link
                  to={item.url || "#"}
                  className={`sidebar-link ${isParentActive(item) ? "active" : "inactive"}`}
                >
                  <item.icon size={20} style={{ marginRight: isSidebarOpen ? "8px" : "0" }} />
                  {isSidebarOpen && <span>{item.title}</span>}
                </Link>
                {isSidebarOpen && item.items && !item.isLink && (
                  <span onClick={(e) =>  {e.stopPropagation();toggleParent(index)}} style={{ cursor: "pointer" }}>
                    {openParents.includes(index) ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}
              </div>
              {item.items && openParents.includes(index) && isSidebarOpen && (
                <div className="submenu">
                  {item.items.map((sub, subIndex) => (
                    <div key={sub.title}>
                      {sub.items ? (
                        // Sub-parent menu item (FG Master, PM Master)
                        <>
                          <div
                            className={`submenu-link ${isSubParentActive(sub) ? "active" : ""}`}
                            style={{ justifyContent: 'space-between', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleSubParent(index, subIndex);
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {sub.icon && <sub.icon size={18} style={{ marginRight: "8px" }} />}
                              <span>{sub.title}</span>
                            </div>
                            <span>
                              {openSubParents.includes(`${index}-${subIndex}`) ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                            </span>
                          </div>
                          {openSubParents.includes(`${index}-${subIndex}`) && (
                            <div className="sub-submenu">
                              {sub.items.map((subSub) => (
                                <Link
                                  to={subSub.url}
                                  key={subSub.title}
                                  className={`sub-submenu-link ${isActive(subSub.url) ? "active" : ""}`}
                                >
                                  <subSub.icon size={16} style={{ marginRight: "8px" }} />
                                  <span>{subSub.title}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={sub.url}
                          className={`submenu-link ${isActive(sub.url) ? "active" : ""}`}
                        >
                          {sub.icon && <sub.icon size={18} style={{ marginRight: "8px" }} />}
                          <span>{sub.title}</span>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      )}
    </div>
  ) : null;
};

export default Sidebar;